import React, { useState, useEffect } from 'react';
import { specialistsApi } from '../../services/apiService';
import './SpecialistEditModal.css';

const SpecialistEditModal = ({ isOpen, onClose, specialist, onUpdate }) => {
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    title: '',
    category: '',
    expertise: '',
    experience: '',
    country: '',
    city: '',
    companyName: '',
    bio: '',
    membershipPlan: 'basic',
    status: 'active',
    interestedLocations: '',
    avatar: '',
    coverImage: ''
  });

  const categoryOptions = [
    'Architecture', 'Civil Engineering', 'Electrical', 'Mechanical',
    'Structural Engineering', 'Interior Design', 'Landscape Architecture',
    'Environmental Engineering', 'Project Management', 'Urban Planning',
    'Construction Management', 'MEP Engineering', 'Retail Design',
    'Transportation Engineering', 'Industrial Architecture'
  ];

  const statusOptions = [
    { value: 'active', label: 'Active' },
    { value: 'pending', label: 'Pending' },
    { value: 'inactive', label: 'Inactive' },
    { value: 'suspended', label: 'Suspended' }
  ];

  const membershipOptions = [
    { value: 'basic', label: 'Basic' },
    { value: 'gcc-business', label: 'GCC Business' },
    { value: 'global-enterprise', label: 'Global Enterprise' }
  ];

  useEffect(() => {
    if (specialist && isOpen) {
      setFormData({
        name: specialist.name || '',
        email: specialist.email || '',
        phone: specialist.phone || '',
        title: specialist.title || '',
        category: specialist.category || '',
        expertise: specialist.expertise || '',
        experience: specialist.experience || '',
        country: specialist.country || '',
        city: specialist.city || '',
        companyName: specialist.companyName || '',
        bio: specialist.bio || '',
        membershipPlan: specialist.membershipPlan || 'basic',
        status: specialist.status || 'active',
        interestedLocations: specialist.interestedLocations || '',
        avatar: specialist.avatar || '',
        coverImage: specialist.coverImage || ''
      });
      setActiveTab('basic');
    }
  }, [specialist, isOpen]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.email.trim()) {
      alert('Name and Email are required');
      return;
    }
    try {
      setSaving(true);
      await specialistsApi.update(specialist.id, formData);
      if (onUpdate) onUpdate();
      onClose();
    } catch (err) {
      console.error('Error updating specialist:', err);
      alert(err.response?.data?.error || 'Failed to update specialist');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="specialist-edit-modal-overlay" onClick={onClose}>
      <div className="specialist-edit-modal" onClick={e => e.stopPropagation()}>
        <div className="specialist-edit-header">
          <h2>Edit Specialist</h2>
          <button className="close-btn" onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        <div className="specialist-edit-tabs">
          <button className={`tab-btn ${activeTab === 'basic' ? 'active' : ''}`} onClick={() => setActiveTab('basic')}>Basic Info</button>
          <button className={`tab-btn ${activeTab === 'professional' ? 'active' : ''}`} onClick={() => setActiveTab('professional')}>Professional</button>
          <button className={`tab-btn ${activeTab === 'contact' ? 'active' : ''}`} onClick={() => setActiveTab('contact')}>Contact</button>
          <button className={`tab-btn ${activeTab === 'settings' ? 'active' : ''}`} onClick={() => setActiveTab('settings')}>Settings</button>
        </div>

        <form onSubmit={handleSubmit} className="specialist-edit-form">
          {activeTab === 'basic' && (
            <div className="form-section">
              <div className="form-group">
                <label>Full Name *</label>
                <input type="text" name="name" value={formData.name} onChange={handleInputChange} required />
              </div>
              <div className="form-group">
                <label>Title</label>
                <input type="text" name="title" value={formData.title} onChange={handleInputChange} placeholder="e.g., Senior Architect" />
              </div>

              <div className="form-group">
                <label>Category</label>
                <select name="category" value={formData.category} onChange={handleInputChange}>
                  <option value="">Select Category</option>
                  {categoryOptions.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Experience</label>
                <input type="text" name="experience" value={formData.experience} onChange={handleInputChange} placeholder="e.g., 10 years" />
              </div>

              <div className="form-group">
                <label>Company</label>
                <input type="text" name="companyName" value={formData.companyName} onChange={handleInputChange} />
              </div>
              <div className="form-group">
                <label>Expertise</label>
                <input type="text" name="expertise" value={formData.expertise} onChange={handleInputChange} placeholder="e.g., Residential, Commercial" />
              </div>

              <div className="form-group full-width">
                <label>Bio</label>
                <textarea name="bio" value={formData.bio} onChange={handleInputChange} rows={4} placeholder="Brief biography..." />
              </div>
            </div>
          )}

          {activeTab === 'professional' && (
            <div className="form-section">
              <div className="form-group full-width">
                <label>Interested Locations</label>
                <input type="text" name="interestedLocations" value={formData.interestedLocations} onChange={handleInputChange} placeholder="e.g., Dubai, Qatar, Saudi Arabia" />
              </div>

              <div className="form-group">
                <label>Avatar URL</label>
                <input type="text" name="avatar" value={formData.avatar} onChange={handleInputChange} placeholder="https://example.com/avatar.jpg" />
              </div>
              <div className="form-group">
                <label>Cover Image URL</label>
                <input type="text" name="coverImage" value={formData.coverImage} onChange={handleInputChange} placeholder="https://example.com/cover.jpg" />
              </div>
            </div>
          )}

          {activeTab === 'contact' && (
            <div className="form-section">
              <div className="form-group">
                <label>Email *</label>
                <input type="email" name="email" value={formData.email} onChange={handleInputChange} required />
              </div>
              <div className="form-group">
                <label>Phone</label>
                <input type="text" name="phone" value={formData.phone} onChange={handleInputChange} placeholder="+971 XX XXX XXXX" />
              </div>

              <div className="form-group">
                <label>Country</label>
                <input type="text" name="country" value={formData.country} onChange={handleInputChange} />
              </div>
              <div className="form-group">
                <label>City</label>
                <input type="text" name="city" value={formData.city} onChange={handleInputChange} />
              </div>
            </div>
          )}

          {activeTab === 'settings' && (
            <div className="form-section">
              <div className="form-group">
                <label>Status</label>
                <select name="status" value={formData.status} onChange={handleInputChange}>
                  {statusOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Membership Plan</label>
                <select name="membershipPlan" value={formData.membershipPlan} onChange={handleInputChange}>
                  {membershipOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>
          )}

          <div className="form-actions">
            <button type="button" className="cancel-btn" onClick={onClose}>Cancel</button>
            <button type="submit" className="save-btn" disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SpecialistEditModal;
